import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useAppMode } from '../context/DemoContext';
import { useLocation, Link } from 'react-router-dom';

/**
 * Persistent DEVNET pill — bottom-right corner when in Live Mode.
 * Mirrors DemoBanner but with a cyan/violet "on-chain" aesthetic.
 * Hover reveals an Exit (X) overlay and a "DEVNET · LIVE MODE" tooltip.
 * Tucks itself out of the way on /arcade to keep gameplay distraction-free.
 */
export default function DevnetBadge() {
  const { isLive, exit } = useAppMode();
  const { pathname } = useLocation();

  if (!isLive || pathname.startsWith('/arcade')) return null;

  // Stack above DemoBanner if both ever showed (defensive — shouldn't happen).
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.2, type: 'spring', stiffness: 200 }}
      className="fixed bottom-6 right-6 z-[200] group"
      data-testid="devnet-badge"
    >
      {/* Outer pulsing rings */}
      <motion.div
        className="absolute inset-[-6px] rounded-full"
        style={{ border: '1px solid rgba(125,208,255,0.16)' }}
        animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute inset-[-12px] rounded-full"
        style={{ border: '1px solid rgba(168,146,255,0.10)' }}
        animate={{ scale: [1, 1.4, 1], opacity: [0.25, 0, 0.25] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      />

      {/* Glow halo */}
      <motion.div
        className="absolute inset-[-4px] rounded-full"
        animate={{
          boxShadow: [
            '0 0 16px 3px rgba(125,208,255,0.22), 0 0 32px 6px rgba(168,146,255,0.06)',
            '0 0 26px 5px rgba(125,208,255,0.30), 0 0 42px 8px rgba(168,146,255,0.10)',
            '0 0 16px 3px rgba(125,208,255,0.22), 0 0 32px 6px rgba(168,146,255,0.06)',
          ],
        }}
        transition={{ duration: 3, repeat: Infinity }}
      />

      {/* Main orb */}
      <div
        className="relative rounded-full flex items-center justify-center overflow-hidden cursor-default px-3.5 h-11"
        style={{
          background: 'radial-gradient(circle at 40% 35%, #08151f, #04080f)',
          border: '1.5px solid rgba(125,208,255,0.45)',
          minWidth: 88,
        }}
      >
        {/* Inner conic shimmer */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'conic-gradient(from 0deg, transparent, rgba(125,208,255,0.14), transparent, rgba(168,146,255,0.08), transparent)',
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
        />

        {/* Pulse dot + label */}
        <span
          className="relative inline-flex items-center gap-2 z-10"
          data-testid="devnet-badge-label"
        >
          <motion.span
            className="inline-block rounded-full"
            style={{
              width: 7,
              height: 7,
              background: '#7dd0ff',
              boxShadow: '0 0 8px #7dd0ff, 0 0 16px rgba(125,208,255,0.6)',
            }}
            animate={{ opacity: [0.5, 1, 0.5], scale: [0.9, 1.1, 0.9] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <span
            className="text-[10px] tracking-[0.22em] font-bold uppercase"
            style={{
              fontFamily: "'Cinzel', serif",
              color: '#a8def7',
              textShadow: '0 0 8px rgba(125,208,255,0.55)',
            }}
          >
            DEVNET
          </span>
        </span>

        {/* Hover Exit overlay */}
        <button
          onClick={exit}
          data-testid="devnet-exit-btn"
          className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20"
          style={{ background: 'rgba(4,8,15,0.92)' }}
          aria-label="Exit live mode"
        >
          <X className="w-3.5 h-3.5" style={{ color: 'rgba(125,208,255,0.85)' }} />
        </button>
      </div>

      {/* Tooltip — appears on hover */}
      <Link
        to="/codex/api"
        className="absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap px-3 py-1.5 rounded-full hover:brightness-125"
        style={{
          background: 'rgba(4,8,15,0.92)',
          border: '1px solid rgba(125,208,255,0.3)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <span
          className="text-[9px] tracking-[0.18em] uppercase font-bold"
          style={{ fontFamily: "'Cinzel', serif", color: 'rgba(125,208,255,0.78)' }}
        >
          Live · view program
        </span>
      </Link>
    </motion.div>
  );
}
